import React from 'react';
import styled from 'styled-components';
import Modal from '../modal';
import Button from '../button';
import { MAX_PART_SECONDS, formatFootage } from '../../helpers/playlistDownloadParts';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  text-align: left;
  color: ${(props) => props.theme.white};
  font-family: "SatoshiRegular";
`;

const Title = styled.h2`
  margin: 0;
  color: ${(props) => props.theme.white};
  font-family: "SpaceGrotesk";
  font-size: 26px;
  font-weight: 500;
  line-height: 34px;
`;

const Subtitle = styled.p`
  margin: -8px 0 0;
  color: ${(props) => props.theme.blueish};
  font-size: 16px;
  overflow-wrap: anywhere;
`;

const Summary = styled.p`
  margin: 0;
  color: ${(props) => props.theme.white};
  font-family: "SatoshiMedium";
  font-size: 15px;
`;

const Note = styled.p`
  margin: 0;
  color: ${(props) => props.theme.blueish};
  font-size: 14px;
  line-height: 20px;
`;

const PartList = styled.ol`
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  max-height: 45vh;
  overflow-y: auto;
  border-top: 1px solid ${(props) => props.theme.lines};
`;

const PartRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 4px 12px 0;
  border-bottom: 1px solid ${(props) => props.theme.lines};
`;

const PartName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;

  strong {
    font-family: "SatoshiBold";
    font-size: 15px;
    font-weight: 400;
  }

  small {
    color: ${(props) => props.theme.blueish2};
    font-size: 13px;
  }
`;

const PartStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px 12px;
  color: ${(props) => (props.$failed ? props.theme.red : props.theme.blueish)};
  font-size: 14px;
  line-height: 20px;
  text-align: right;
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 104px;
  height: 34px;
  padding: 0 14px;
  border-radius: 4px;
  background-color: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  font-family: "SatoshiBold";
  font-size: 14px;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.white};
    outline-offset: 2px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 6px;
`;

const FooterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const formatWait = (seconds) => (seconds >= 120 ? `${Math.round(seconds / 60)} min` : `${seconds} s`);

const partStatusText = (part) => {
  switch (part.status) {
    case 'preparing':
      return part.attempt > 0 ? 'Preparing again…' : 'Preparing…';
    case 'retrying':
      return `Taking longer than usual. Trying again in ${formatWait(part.retryInSeconds)}.`;
    case 'failed':
      return part.error;
    case 'stopped':
      return 'Stopped';
    default:
      return 'Waiting';
  }
};

export const PlaylistDownloadModal = ({ download, onStop, onResume, onClose, onPartDownloaded }) => {
  if (!download) return null;

  const { title, clipCount, skippedCount, footageSeconds, running, parts } = download;
  const readyCount = parts.filter((part) => part.status === 'ready').length;
  const unfinishedCount = parts.length - readyCount;

  return (
    <Modal
      isOpen
      setIsOpen={(open) => !open && onClose()}
      closeIc
      unstyledTypography
      width="720px"
      padding="48px 40px 32px"
      mobilePadding="48px 20px 24px"
      contentWidth="100%"
      contentMobileWidth="100%"
      contentTextAlign="left"
    >
      <Panel>
        <Title>Download in parts</Title>
        {title && <Subtitle>{title}</Subtitle>}
        <Summary>
          {clipCount} clips · {formatFootage(footageSeconds)} of video · {parts.length} parts
        </Summary>
        <Note>
          This playlist is too long to prepare as one file, so it’s split into ZIP files of up
          to {MAX_PART_SECONDS / 60} minutes of video each. Parts are prepared one at a time: keep
          this page open and download each part when its button appears.
        </Note>
        {skippedCount > 0 && (
          <Note>{skippedCount} clip(s) without a valid video source or time range were left out.</Note>
        )}

        <PartList>
          {parts.map((part) => (
            <PartRow key={part.number}>
              <PartName>
                <strong>Part {part.number}</strong>
                <small>
                  {part.firstClip === part.lastClip
                    ? `Clip ${part.firstClip}`
                    : `Clips ${part.firstClip}–${part.lastClip}`}
                  {' · '}
                  {formatFootage(part.seconds)}
                </small>
              </PartName>
              {part.status === 'ready' ? (
                <PartStatus>
                  {part.downloaded && <span>Downloaded</span>}
                  <DownloadLink
                    href={part.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onPartDownloaded(part.number)}
                  >
                    Download
                  </DownloadLink>
                </PartStatus>
              ) : (
                <PartStatus $failed={part.status === 'failed'}>{partStatusText(part)}</PartStatus>
              )}
            </PartRow>
          ))}
        </PartList>

        <Footer>
          <Summary aria-live="polite">
            {readyCount} of {parts.length} parts ready
          </Summary>
          <FooterButtons>
            {running && <Button title="Stop" size="small" color="secondary" onClick={onStop} />}
            {!running && unfinishedCount > 0 && (
              <Button title="Retry unfinished parts" size="large" onClick={onResume} />
            )}
            <Button title="Close" size="small" color="secondary" onClick={onClose} />
          </FooterButtons>
        </Footer>
        {unfinishedCount > 0 && (
          <Note>
            Closing stops the parts that aren’t ready yet. Parts that were already prepared come back
            quickly if you start the download again.
          </Note>
        )}
      </Panel>
    </Modal>
  );
};
